import { useCreateItem, useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { FormikItemsAutocomplete } from '@components/Forms';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid
} from '@mui/material';
import {
	MAX_CHARS_AUTHORIZATION,
	stringRequiredMaxCharsValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React from 'react';
import { IPermission, IRole } from 'types/authorization';
import { IActionDialogProps } from 'types/common';
import { object } from 'yup';

const RoleCreateDialog: React.FC<IActionDialogProps> = ({
	onCreate,
	onCancel
}) => {
	const { invokeToastableAction } = useToast();
	const { data } = useItems<IPermission>(
		QueryKeys.permissions,
		API_ROUTES.v1.permissions.all
	);
	const generateItemMutation = useCreateItem<IRole>(
		QueryKeys.roles,
		API_ROUTES.v1.roles.add
	);

	const formik = useFormik<IRole>({
		initialValues: {
			name: '',
			description: '',
			default: false,
			permissions: []
		},
		validationSchema: object({
			name: stringRequiredMaxCharsValidationScheme(MAX_CHARS_AUTHORIZATION)
		}),
		onSubmit: async (values: IRole) => {
			await invokeToastableAction(
				() => generateItemMutation.mutateAsync(values),
				'roles.action.add.dialog.toast',
				onCreate
			);
		}
	});

	return (
		<Dialog open maxWidth="xs" data-testid="role-create-dialog">
			<DialogTitle>{intl.tr('roles.action.add.dialog.title')}</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextFieldValidation
								id="name"
								formik={formik}
								fullWidth
								autoFocus
								required
								label={intl.tr('fields.roles.column.name')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextFieldValidation
								id="description"
								formik={formik}
								fullWidth
								label={intl.tr('fields.roles.column.description')}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormikItemsAutocomplete
								id="permissions"
								formik={formik}
								data={data?.data || []}
								textLabel={intl.tr(
									'roles.action.edit.dialog.field.permissions'
								)}
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} data-testid="roles-add-dialog-cancel-button">
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="roles-add-dialog-approve-button"
				>
					{intl.tr('button.add')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default RoleCreateDialog;
